import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { UserPlus, Mail, Lock, User, Phone } from 'lucide-react';

const RegisterPage = ({ userType = 'client' }) => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const role = userType === 'landlord' ? 'landlord' : 'client';
      const user = await register(
        formData.email,
        formData.password,
        formData.full_name,
        formData.phone,
        role
      );
      
      toast.success('Registration Successful!', {
        description: `Welcome, ${user.full_name}!`
      });

      if (user.role === 'landlord') {
        navigate('/landlord/dashboard');
      } else if (user.role === 'admin') {
        navigate('/super-admin');
      } else {
        navigate('/client/dashboard');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration Failed', {
        description: error.response?.data?.detail || 'An error occurred'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
      <Navbar />
      <Toaster position="top-center" richColors />

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-md">
          <Card className="rounded-2xl shadow-2xl border-2" style={{ borderColor: '#e8e8e8' }}>
            <CardContent className="p-10">
              <div className="text-center mb-8">
                <div className="inline-block p-4 rounded-full mb-4" style={{ background: 'linear-gradient(135deg, rgba(212,165,116,0.1), rgba(232,201,161,0.1))' }}>
                  <UserPlus size={48} style={{ color: '#d4a574' }} />
                </div>
                <h1 className="text-4xl font-bold mb-2" style={{ color: '#1a1a2e' }}>
                  {userType === 'admin' ? 'Admin Registration' : userType === 'landlord' ? 'Landlord Registration' : 'Client Registration'}
                </h1>
                <p style={{ color: '#636e72' }}>
                  {userType === 'admin'
                    ? 'Create super admin account to manage the platform'
                    : userType === 'landlord' 
                    ? 'Create an account to manage your properties'
                    : 'Create an account to place bids and book properties'
                  }
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="full_name" className="flex items-center gap-2 mb-2">
                    <User size={16} style={{ color: '#d4a574' }} />
                    <span style={{ color: '#2d3436' }}>Full Name</span>
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                    className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                    <Mail size={16} style={{ color: '#d4a574' }} />
                    <span style={{ color: '#2d3436' }}>Email Address</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
                    <Phone size={16} style={{ color: '#d4a574' }} />
                    <span style={{ color: '#2d3436' }}>Phone Number</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+44 7700 900000"
                    className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="flex items-center gap-2 mb-2">
                    <Lock size={16} style={{ color: '#d4a574' }} />
                    <span style={{ color: '#2d3436' }}>Password</span>
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    minLength={6}
                    className="h-11 rounded-xl border-2 focus:border-[#d4a574]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary-custom h-12 rounded-full text-lg font-semibold mt-6"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p style={{ color: '#636e72' }}>
                  Already have an account?{' '}
                  <Link 
                    to={userType === 'landlord' ? '/landlord/login' : '/login'}
                    className="font-semibold"
                    style={{ color: '#d4a574' }}
                  >
                    Login here
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RegisterPage;
